import create from 'zustand';
import ApiClient from '../networking/fetch';
import {
  EntityWisePerformance,
  PaperCodeWisePerformance,
  Participation,
  SubjectClassWisePerformance,
  SubmoduleWisePerformance,
} from './types';

export interface ReportState {
  subEntities?: string[];
  currentParticipation?: Participation;
  currentSubClassPerformance?: SubjectClassWisePerformance;
  currentEntityWisePerformance?: EntityWisePerformance;
  currentEntityWisePaperTypePerformance?: any;
  currentSubmodulePerformance?: SubmoduleWisePerformance;
  currentPaperCodePerformance?: PaperCodeWisePerformance;
  distinctDataSources?: string[];
  distinctGrades?: any;
  baselineSurveyIds?: any[];
  progressSurveyIds?: any[];
  selectedDataSouce: string;
  reportType: string;
  ReportTemplate: string;
  lowerLimit: number;
  higherLimit: number;
  compareSurveyReportDrawerIsOpen: boolean;
  compareStatisticsReportDrawerIsOpen: boolean;
  fetchParticipation: (
    base: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => void;
  fetchComparativePerformance: (
    base: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => void;
  fetchEntityWisePerformance: (
    base: string,
    grade: string,
    subject: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => void;
  fetchEntityWisePaperTypePerformance: (
    base: string,
    grade: string,
    subject: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => void;
  fetchSubmodulePerformance: (
    base: string,
    grade: string,
    subject: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => void;
  fetchPaperCodePerformance: (
    base: string,
    grade: string,
    subject: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => void;
  fetchDistictDatasource: (showLoader: (param: boolean) => void) => void;
  setReportType: (reportType: string) => void;
  setReportTemplate: (ReportTemplate: string) => void;
  setLowerLimit: (lowerLimit: number) => void;
  setHigherLimit: (higherLimit: number) => void;
  setCompareSurveyReportDrawerIsOpen: (isOpen: boolean) => void;
  setCompareStatisticsReportDrawerIsOpen: (isOpen: boolean) => void;
  setSelectedDataSource: (dataSource: string) => void;
  fetchDistictGrades: (
    base: string,
    startDate: number,
    endDate: number,
    dataSource: string
  ) => void;
  fetchBaseLineSurveyIds: (
    base: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    grade: string,
    subject: string,
    showLoader: any
  ) => void;
  fetchProgressSurveyIds: (
    base: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    grade: string,
    subject: string,
    showLoader: any
  ) => void;
  setBaseLineSurveyIds: (ids: any) => void;
  setProgressSurveyIds: (ids: any) => void;
  setDistinctGrades: (grades: any) => void;
  setCurrentParticipation: (data: any) => void;
  setCurrentSubClassPerformance: (data: any) => void;
  setCurrentEntityWisePerformance: (data: any) => void;
  setSubEntities: (data: any) => void;
  setCurrentEntityWisePaperTypePerformance: (data: any) => void;
  setCurrentSubmodulePerformance: (data: any) => void;
  setCurrentPaperCodePerformance: (data: any) => void;
}

export const useNewStore = create<ReportState>((set) => ({
  selectedDataSouce: '',
  reportType: 'bySchool',
  ReportTemplate: 'compareReports',
  lowerLimit: 33,
  higherLimit: 70,
  compareSurveyReportDrawerIsOpen: false,
  compareStatisticsReportDrawerIsOpen: false,
  fetchParticipation: async (
    base: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => {
    showLoader(true);
    const response = await ApiClient.get(
      `${process.env.REACT_APP_BACKEND_URL}/participation/${base}`,
      { startDate, endDate, dataSource }
    );
    showLoader(false);
    set((originalState) => {
      return {
        ...originalState,
        currentParticipation: response.data,
      };
    });
  },
  setCurrentParticipation: (data: any) => {
    set((originalState) => {
      return {
        ...originalState,
        currentParticipation: data,
      };
    });
  },
  fetchComparativePerformance: async (
    base: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => {
    showLoader(true);
    const response = await ApiClient.get(
      `${process.env.REACT_APP_BACKEND_URL}/compare/${base}`,
      { startDate, endDate, dataSource }
    );
    showLoader(false);
    set((originalState) => {
      return {
        ...originalState,
        currentSubClassPerformance: response.data,
      };
    });
  },
  setCurrentSubClassPerformance: (data: any) => {
    set((originalState) => {
      return {
        ...originalState,
        currentSubClassPerformance: data,
      };
    });
  },
  fetchEntityWisePerformance: async (
    base: string,
    grade: string,
    subject: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => {
    try {
      showLoader(true);
      const response = await ApiClient.get(
        `${process.env.REACT_APP_BACKEND_URL}/performance/${base}`,
        { grade, subject, startDate, endDate, dataSource }
      );
      showLoader(false);
      const data: EntityWisePerformance = response.data;
      set((originalState) => {
        return {
          ...originalState,
          currentEntityWisePerformance: data,
          subEntities: data?.overall.names,
        };
      });
    } catch (error) {
      set((originalState) => {
        return {
          ...originalState,
          subEntities: [],
        };
      });
    }
  },
  setCurrentEntityWisePerformance: (data: any) => {
    set((originalState) => {
      return {
        ...originalState,
        currentEntityWisePerformance: data,
      };
    });
  },
  setSubEntities: (data: any) => {
    set((originalState) => {
      return {
        ...originalState,
        subEntities: data,
      };
    });
  },
  fetchEntityWisePaperTypePerformance: async (
    base: string,
    grade: string,
    subject: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => {
    try {
      showLoader(true);
      const response = await ApiClient.get(
        `${process.env.REACT_APP_BACKEND_URL}/performance/${base}`,
        { grade, subject, startDate, endDate, dataSource }
      );
      showLoader(false);
      const data: any = response.data;
      set((originalState) => {
        return {
          ...originalState,
          currentEntityWisePaperTypePerformance: data,
        };
      });
    } catch (error) {
      set((originalState) => {
        return {
          ...originalState,
        };
      });
    }
  },
  setCurrentEntityWisePaperTypePerformance: (data: any) => {
    set((originalState) => {
      return {
        ...originalState,
        currentEntityWisePaperTypePerformance: data,
      };
    });
  },
  fetchSubmodulePerformance: async (
    base: string,
    grade: string,
    subject: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => {
    showLoader(true);
    const response = await ApiClient.get(
      `${process.env.REACT_APP_BACKEND_URL}/submodules/${base}`,
      { grade, subject, startDate, endDate, dataSource }
    );
    showLoader(false);
    const data: SubmoduleWisePerformance = response.data;
    set((originalState) => {
      return {
        ...originalState,
        currentSubmodulePerformance: data,
      };
    });
  },
  setCurrentSubmodulePerformance: (data: any) => {
    set((originalState) => {
      return {
        ...originalState,
        currentSubmodulePerformance: data,
      };
    });
  },
  fetchPaperCodePerformance: async (
    base: string,
    grade: string,
    subject: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    showLoader: (param: boolean) => void
  ) => {
    showLoader(true);
    const response = await ApiClient.get(
      `${process.env.REACT_APP_BACKEND_URL}/paperCode/${base}`,
      { grade, subject, startDate, endDate, dataSource }
    );
    showLoader(false);
    const data: PaperCodeWisePerformance = response.data;
    set((originalState) => {
      return {
        ...originalState,
        currentPaperCodePerformance: data,
      };
    });
  },
  setCurrentPaperCodePerformance: (data: any) => {
    set((originalState) => {
      return {
        ...originalState,
        currentPaperCodePerformance: data,
      };
    });
  },
  fetchDistictDatasource: async (showLoader: (param: boolean) => void) => {
    showLoader(true);
    const response = await ApiClient.get(
      `${process.env.REACT_APP_BACKEND_URL}/reports/distinct-datasouce`,
      {}
    );
    showLoader(false);
    const data: string[] = response.data;
    set((originalState) => {
      return {
        ...originalState,
        selectedDataSouce: data.length > 0 ? data[0] : '',
        distinctDataSources: data,
      };
    });
  },
  setSelectedDataSource: (source: string) => {
    set((originalState) => {
      return {
        ...originalState,
        selectedDataSouce: source,
      };
    });
  },
  setReportType: (reportType: string) => {
    set((originalState) => {
      return {
        ...originalState,
        reportType: reportType,
      };
    });
  },
  setBaseLineSurveyIds: (ids: any) => {
    set((originalState) => {
      return {
        ...originalState,
        baselineSurveyIds: ids,
      };
    });
  },
  setProgressSurveyIds: (ids: any) => {
    set((originalState) => {
      return {
        ...originalState,
        progressSurveyIds: ids,
      };
    });
  },
  setReportTemplate: (ReportTemplate: string) => {
    set((originalState) => {
      return {
        ...originalState,
        ReportTemplate: ReportTemplate,
      };
    });
  },
  setLowerLimit: (lowerLimit: number) => {
    set((originalState) => {
      return {
        ...originalState,
        lowerLimit: lowerLimit,
      };
    });
  },
  setHigherLimit: (higherLimit: number) => {
    set((originalState) => {
      return {
        ...originalState,
        higherLimit: higherLimit,
      };
    });
  },
  setCompareSurveyReportDrawerIsOpen: (isOpen: boolean) => {
    set((originalState) => {
      return {
        ...originalState,
        compareSurveyReportDrawerIsOpen: isOpen,
      };
    });
  },
  setDistinctGrades: (grades: any) => {
    set((originalState) => {
      return {
        ...originalState,
        distinctGrades: grades,
      };
    });
  },
  setCompareStatisticsReportDrawerIsOpen: (isOpen: boolean) => {
    set((originalState) => {
      return {
        ...originalState,
        compareStatisticsReportDrawerIsOpen: isOpen,
      };
    });
  },
  fetchDistictGrades: async (
    base: string,
    startDate: number,
    endDate: number,
    dataSource: string
  ) => {
    const response = await ApiClient.get(
      `${process.env.REACT_APP_BACKEND_URL}/distinct-grade-subject-surveyId/${base}`,
      { startDate, endDate, dataSource }
    );
    const data: any[] = response.data;
    let rowMap = new Map();
    data.forEach((row) => {
      const grade = row.grade;
      const subject = row.subject;
      const surveyId = row.surveyId;
      if (!rowMap.has(grade)) {
        rowMap.set(grade, new Map());
      }
      let gradeObject = rowMap.get(grade);
      if (!gradeObject.has(subject)) {
        gradeObject.set(subject, []);
      }
      let surveyIdArray = gradeObject.get(subject);
      surveyIdArray.push(surveyId);
    });
    set((originalState) => {
      return {
        ...originalState,
        distinctGrades: rowMap,
      };
    });
  },
  fetchBaseLineSurveyIds: async (
    base: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    grade: string,
    subject: string,
    showLoader: any
  ) => {
    showLoader(true);
    const response = await ApiClient.get(
      `${process.env.REACT_APP_BACKEND_URL}/distinct-baseline-surveyIds/${base}`,
      { startDate, endDate, dataSource, grade, subject }
    );
    showLoader(false);
    set((originalState) => {
      return {
        ...originalState,
        baselineSurveyIds: response.data.map((key: any) => ({
          label: key,
          value: key,
        })),
      };
    });
  },
  fetchProgressSurveyIds: async (
    base: string,
    startDate: number,
    endDate: number,
    dataSource: string,
    grade: string,
    subject: string,
    showLoader: any
  ) => {
    showLoader(true);
    const response = await ApiClient.get(
      `${process.env.REACT_APP_BACKEND_URL}/distinct-progress-surveyIds/${base}`,
      { startDate, endDate, dataSource, grade, subject }
    );
    showLoader(false);
    set((originalState) => {
      return {
        ...originalState,
        progressSurveyIds: response.data.map((key: any) => ({
          label: key,
          value: key,
        })),
      };
    });
  },
}));
